import { useState } from 'react'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Checkbox } from '../ui/checkbox'
import { useEmployees } from '../../hooks/useEmployees'
import { useRoles } from '../../hooks/useRoles'
import { useDepartmentsForSelect } from '../../hooks/useDepartments'
import { useOrganizationsForSelect } from '../../hooks/organizations/useOrganizations'
import { Input } from '../ui/input'
import { LoadingSpinner } from '../ui/loading-states'

export type RecipientType = 'employee' | 'role' | 'department' | 'broadcast'

interface RecipientSelectorProps {
  recipientType: RecipientType
  recipientId: string
  departmentIds: string[]
  onRecipientTypeChange: (type: RecipientType) => void
  onRecipientIdChange: (id: string) => void
  onDepartmentIdsChange: (ids: string[]) => void
  disabled?: boolean
}

export function RecipientSelector({
  recipientType,
  recipientId,
  departmentIds,
  onRecipientTypeChange,
  onRecipientIdChange,
  onDepartmentIdsChange,
  disabled = false,
}: RecipientSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [organizationId, setOrganizationId] = useState('')

  // Fetch data based on recipient type
  const employeesQuery = useEmployees({ search: searchQuery, limit: 10000, isActive: true })
  const rolesQuery = useRoles({})
  const organizationsQuery = useOrganizationsForSelect(recipientType === 'department')
  const departmentsQuery = useDepartmentsForSelect(organizationId || null)

  const handleTypeChange = (value: string) => {
    onRecipientTypeChange(value as RecipientType)
    onRecipientIdChange('') // Reset recipient ID when type changes
    onDepartmentIdsChange([])
    setOrganizationId('')
  }

  const handleOrganizationChange = (value: string) => {
    setOrganizationId(value)
    // Отделы принадлежат организации — при её смене прошлый выбор невалиден
    onDepartmentIdsChange([])
  }

  const toggleDepartment = (id: string) => {
    onDepartmentIdsChange(
      departmentIds.includes(id)
        ? departmentIds.filter((d) => d !== id)
        : [...departmentIds, id]
    )
  }

  return (
    <div className="space-y-4">
      {/* Recipient Type Selection */}
      <div className="space-y-2">
        <Label htmlFor="recipientType">Тип получателя</Label>
        <Select value={recipientType} onValueChange={handleTypeChange} disabled={disabled}>
          <SelectTrigger id="recipientType">
            <SelectValue placeholder="Выберите тип получателя" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="employee">Сотрудник</SelectItem>
            <SelectItem value="role">Роль</SelectItem>
            <SelectItem value="department">Отдел</SelectItem>
            <SelectItem value="broadcast">Всем сотрудникам</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {recipientType === 'employee' && 'Уведомление будет отправлено выбранному сотруднику'}
          {recipientType === 'role' && 'Уведомление будет отправлено всем пользователям с выбранной ролью'}
          {recipientType === 'department' && 'Уведомление будет отправлено всем сотрудникам выбранных отделов'}
          {recipientType === 'broadcast' && 'Уведомление будет отправлено всем сотрудникам компании'}
        </p>
      </div>

      {/* Recipient Selection (for non-broadcast types) */}
      {recipientType !== 'broadcast' && (
        <div className="space-y-2">
          {recipientType !== 'department' && (
            <Label htmlFor="recipient">
              {recipientType === 'employee' && 'Выберите сотрудника'}
              {recipientType === 'role' && 'Выберите роль'}
            </Label>
          )}

          {/* Employee Search Input */}
          {recipientType === 'employee' && (
            <Input
              type="text"
              placeholder="Поиск сотрудника..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={disabled}
              className="mb-2"
            />
          )}

          {/* Employee Selector */}
          {recipientType === 'employee' && (
            <>
              {employeesQuery.isLoading ? (
                <div className="flex items-center justify-center p-4">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <>
                  <Select value={recipientId} onValueChange={onRecipientIdChange} disabled={disabled}>
                    <SelectTrigger id="recipient">
                      <SelectValue placeholder="Выберите сотрудника" />
                    </SelectTrigger>
                    <SelectContent>
                      {employeesQuery.data?.data.map((employee) => (
                        <SelectItem key={employee.id} value={employee.id}>
                          {employee.first_name} {employee.last_name}
                          {employee.department && ` - ${employee.department.name}`}
                        </SelectItem>
                      ))}
                      {employeesQuery.data?.data.length === 0 && (
                        <div className="p-2 text-sm text-muted-foreground text-center">
                          Сотрудники не найдены
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  {employeesQuery.data?.data && employeesQuery.data.data.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Найдено сотрудников: {employeesQuery.data.data.length}
                      {employeesQuery.data.total > employeesQuery.data.data.length &&
                        ` из ${employeesQuery.data.total} (используйте поиск для уточнения)`}
                    </p>
                  )}
                </>
              )}
            </>
          )}

          {/* Role Selector */}
          {recipientType === 'role' && (
            <>
              {rolesQuery.isLoading ? (
                <div className="flex items-center justify-center p-4">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <Select value={recipientId} onValueChange={onRecipientIdChange} disabled={disabled}>
                  <SelectTrigger id="recipient">
                    <SelectValue placeholder="Выберите роль" />
                  </SelectTrigger>
                  <SelectContent>
                    {rolesQuery.data?.data.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                    {rolesQuery.data?.data.length === 0 && (
                      <div className="p-2 text-sm text-muted-foreground text-center">
                        Роли не найдены
                      </div>
                    )}
                  </SelectContent>
                </Select>
              )}
            </>
          )}

          {/* Department Selector: организация → отделы (мультивыбор) */}
          {recipientType === 'department' && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="organization">Организация</Label>
                {organizationsQuery.isLoading ? (
                  <div className="flex items-center justify-center p-4">
                    <LoadingSpinner size="sm" />
                  </div>
                ) : (
                  <Select
                    value={organizationId}
                    onValueChange={handleOrganizationChange}
                    disabled={disabled}
                  >
                    <SelectTrigger id="organization">
                      <SelectValue placeholder="Выберите организацию" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizationsQuery.data?.map((org: { id: string; name: string }) => (
                        <SelectItem key={org.id} value={org.id}>
                          {org.name}
                        </SelectItem>
                      ))}
                      {organizationsQuery.data?.length === 0 && (
                        <div className="p-2 text-sm text-muted-foreground text-center">
                          Организации не найдены
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {organizationId && (
                <div className="space-y-2">
                  <Label>Отделы организации</Label>
                  {departmentsQuery.isLoading ? (
                    <div className="flex items-center justify-center p-4">
                      <LoadingSpinner size="sm" />
                    </div>
                  ) : departmentsQuery.data && departmentsQuery.data.length > 0 ? (
                    <>
                      <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border p-2">
                        {departmentsQuery.data.map((department) => (
                          <label
                            key={department.id}
                            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted/50"
                          >
                            <Checkbox
                              checked={departmentIds.includes(department.id)}
                              onCheckedChange={() => toggleDepartment(department.id)}
                              disabled={disabled}
                            />
                            <span className="flex-1">{department.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {department.employee_count} сотр.
                            </span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Выбрано отделов: {departmentIds.length}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      В этой организации нет отделов
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {recipientType !== 'department' && !recipientId && (
            <p className="text-xs text-destructive">Выберите получателя</p>
          )}
          {recipientType === 'department' && departmentIds.length === 0 && (
            <p className="text-xs text-destructive">
              {organizationId
                ? 'Выберите хотя бы один отдел'
                : 'Сначала выберите организацию'}
            </p>
          )}
        </div>
      )}

      {/* Broadcast confirmation */}
      {recipientType === 'broadcast' && (
        <div className="p-3 bg-orange-50 border border-orange-200 rounded-md">
          <p className="text-sm text-orange-800 font-medium">
            ⚠️ Уведомление будет отправлено всем сотрудникам
          </p>
          <p className="text-xs text-orange-600 mt-1">
            Убедитесь, что содержимое подходит для массовой рассылки
          </p>
        </div>
      )}
    </div>
  )
}
